/**
 * The broadcast cut between scenes (`scene-transition`), as pure functions: whether a change of the
 * scene this client's stage shows plays a cut, which kind, and what its title card says. `SceneCut.tsx`
 * runs the timing; this decides.
 *
 * Every change of the shown scene cuts, except the first scene a page load shows: a table-scene change
 * cuts on every client, a Peek or RETURN only on the GM's (the preview is client-local, so only the
 * GM's shown scene moves). Under reduced motion the cut is a short fade with no static and no card.
 */
import type { TableScene } from "@lancer-vtt/domain";
import { sceneLabel } from "./stageScene";

/** `broadcast`: dip to black under static, a title card, fade up. `fade`: a plain fade up from black. */
export type CutKind = "broadcast" | "fade";

/** The title card: the scene's name, plus the pinned world's name over a narrative scene. */
export type CutCard = { title: string; world: string | null };

/** The dip to black, under the static. */
export const DIP_MS = 250;
/** The shortest time the card shows, so a scene that is ready at once still reads as a cut. */
export const CARD_MIN_MS = 350;
/** The card holds at most this long after the swap, ready or not, then fades up anyway. */
export const READY_CEILING_MS = 2000;
/** The fade up into the new scene. */
export const RISE_MS = 350;
/** The whole cut under reduced motion. */
export const REDUCED_FADE_MS = 150;

export function sceneKey(scene: TableScene): string {
  return scene.kind === "encounter" ? `encounter:${scene.encounterId}` : "narrative";
}

/**
 * The cut from the scene the stage shows to the next one, or null for none: `prev` undefined is the
 * page's first scene, and an unchanged scene is no change.
 */
export function sceneCut(args: { prev: TableScene | undefined; next: TableScene; reducedMotion: boolean }): CutKind | null {
  const { prev, next, reducedMotion } = args;
  if (prev === undefined || sceneKey(prev) === sceneKey(next)) return null;
  return reducedMotion ? "fade" : "broadcast";
}

/** The card for a scene: `NARRATIVE` (with the world's name when one is pinned), or the encounter's name. */
export function cutCard(args: { scene: TableScene; nameOf: (encounterId: string) => string | undefined; world: string | null }): CutCard {
  const { scene, nameOf, world } = args;
  return { title: sceneLabel(scene, nameOf), world: scene.kind === "narrative" && world !== null ? world.toUpperCase() : null };
}
